/**
 * An opt-in look inside a chart's series, for a bug only a real browser shows.
 *
 * SciChart needs WebGL and headless Chrome stops at sign-in, so the owner's own
 * browser is the only place a rendering question can be answered. Off unless
 * `localStorage["qf.debug"] === "1"`; then `window.__qfProbe()` reports, per
 * chart, what its price series holds. Nothing here changes what is drawn.
 */

const enabled = (() => {
  try {
    return localStorage.getItem("qf.debug") === "1";
  } catch {
    return false;
  }
})();

const builders = new Map<string, unknown>();

/** Register (or with `null`, forget) a chart's builder — a no-op unless enabled. */
export function debugBuilder(chartGroupId: string, builder: unknown | null): void {
  if (!enabled) return;
  if (builder) builders.set(chartGroupId, builder);
  else builders.delete(chartGroupId);
}

type Loose = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

function probe(): unknown {
  const out: Loose[] = [];
  for (const [id, b] of builders) {
    const panes = (b as Loose).panes as Map<string, Loose> | undefined;
    for (const pane of panes?.values() ?? []) {
      for (const pr of (pane.byId as Map<string, Loose>).values()) {
        if (!/ohlc/i.test(String(pr.alias))) continue;
        const s = pr.series as Loose;
        const pp = s?.paletteProvider as Loose | undefined;
        const n = (pr.values as number[]).length;
        const hex = (v: unknown) => (typeof v === "number" ? (v >>> 0).toString(16) : String(v));
        out.push({
          chart: id,
          alias: pr.alias,
          seriesKind: pr.seriesKind,
          dataKind: pr.dataKind,
          seriesType: s?.type,
          stroke: s?.stroke,
          values: n,
          lastValues: (pr.values as number[]).slice(-5),
          storedPalette: pr.palette ? pr.palette.constructor?.name : null,
          seriesPalette: pp ? pp.constructor?.name : null,
          same: pp === pr.palette,
          colorCount: pr.palette?.colors?.length ?? null,
          lastColors: (pr.palette?.colors as number[] | undefined)?.slice(-5).map(hex) ?? null,
          override: pp?.overrideStrokeArgb
            ? [5, 4, 3, 2, 1].map((k) => hex(pp.overrideStrokeArgb(0, 0, n - k)))
            : null,
          strokeMode: pp?.strokePaletteMode ?? null,
          // What the provider was built from, and what the model says.
          plan: pr.palette?.plan ?? null,
          modelPalette:
            ((b as Loose).paneModels as Loose[] | undefined)
              ?.flatMap((m) => (m.plots ?? []) as Loose[])
              .find((p) => p.id === pr.plotId)?.palette ?? null,
          tokens: {
            positive: (b as Loose).theme?.tokens?.positive,
            negative: (b as Loose).theme?.tokens?.negative,
          },
        });
      }
    }
  }
  return out;
}

/** What each chart's last line/marker hit test considered (charts.md, "The line under the pointer"). */
function hit(): unknown {
  return [...builders].map(([id, b]) => ({ chart: id, ...((b as Loose).lastHitProbe ?? {}) }));
}

/**
 * Where the X axis puts a drawing's ends against the bars beside them — for a
 * line placed at a time **between** two bars (a Seasonality hits block's slot
 * edges). Reads each non-price pane's first lines: their ends in data units, the
 * pixel the axis gives each, and the pixels of the bars either side.
 */
function axis(count = 6): unknown {
  const out: Loose[] = [];
  for (const [id, b] of builders) {
    const times = ((b as Loose).barTimes ?? []) as number[];
    for (const pane of ((b as Loose).panes as Map<string, Loose> | undefined)?.values() ?? []) {
      const sub = pane.sub as Loose;
      const calc = sub?.xAxes?.get(0)?.getCurrentCoordinateCalculator?.();
      if (!calc) continue;
      const px = (t: number) => Math.round(calc.getCoordinate(t) * 10) / 10;
      const lines = (sub.annotations.asArray() as Loose[]).filter((a) => a.type === "RenderContextLineAnnotation" || a.constructor?.name === "LineAnnotation").slice(0, count);
      if (!lines.length) continue;
      const around = (t: number) => {
        let i = times.findIndex((x) => x >= t);
        if (i < 0) i = times.length - 1;
        const prev = times[Math.max(0, i - 1)]!;
        const next = times[i]!;
        return { prev, prevPx: px(prev), next, nextPx: px(next) };
      };
      out.push({
        chart: id,
        lines: lines.map((a) => ({ x1: a.x1, x1Px: px(a.x1), x2: a.x2, x2Px: px(a.x2), y: a.y1, at1: around(a.x1), at2: around(a.x2) })),
      });
    }
  }
  return out;
}

if (enabled) {
  (window as unknown as { __qfAxis?: (n?: number) => unknown }).__qfAxis = axis;
  (window as unknown as { __qfProbe?: () => unknown }).__qfProbe = probe;
  (window as unknown as { __qfHit?: () => unknown }).__qfHit = hit;
}
